import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Fingerprint, LogOut, Timer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ClockInDialog } from "@/components/ClockInDialog";
import { TaskCard } from "@/components/TaskCard";
import { useAuth } from "@/context/AuthContext";
import { api, fmtDateTime, DEPT } from "@/lib/api";

const StatusBar = ({ rec }) => {
  const inNow = rec?.clock_in_time && !rec?.clock_out_time;
  const done = rec?.clock_out_time;
  return (
    <div data-testid="clock-in-status-badge"
      className={`rounded-xl px-4 py-3 flex items-center gap-3 text-white ${inNow ? "bg-emerald-600" : done ? "bg-slate-700" : "bg-rose-500"}`}>
      <span className={`h-2.5 w-2.5 rounded-full bg-white ${inNow ? "pulse-dot" : ""}`} />
      <div className="flex-1">
        <div className="font-heading font-bold text-lg leading-tight">{inNow ? "Clocked In" : done ? "Shift Completed" : "Clocked Out"}</div>
        <div className="text-xs opacity-90">
          {inNow ? `Since ${fmtDateTime(rec.clock_in_time)}` : done ? `${rec.total_hours_worked} hrs · ${rec.status === "half_day" ? "Half Day" : "Present"}` : "You have not clocked in today"}
        </div>
      </div>
    </div>
  );
};

export default function StaffHome() {
  const { user } = useAuth();
  const [rec, setRec] = useState(undefined);
  const [tasks, setTasks] = useState([]);
  const [mode, setMode] = useState(null);
  const [lastMode, setLastMode] = useState("in");
  useEffect(() => {
    if (mode) setLastMode(mode);
  }, [mode]);

  useEffect(() => {
    api.get("/attendance/today").then((r) => setRec(r.data.record));
    api.get("/tasks", { params: { status: "open" } }).then((r) => setTasks(r.data));
  }, []);

  const inNow = rec?.clock_in_time && !rec?.clock_out_time;
  return (
    <div className="space-y-6">
      <div className="fade-up">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500">{DEPT[user.department]} · {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}</div>
        <h1 className="font-heading text-3xl font-extrabold text-slate-900 tracking-tight mt-1" data-testid="staff-greeting">Namaste, {user.full_name.split(" ")[0]}</h1>
      </div>
      {rec === undefined ? <div className="h-16 rounded-xl bg-slate-200 animate-pulse" /> : <StatusBar rec={rec} />}
      {rec !== undefined && !rec?.clock_in_time && (
        <button onClick={() => setMode("in")} data-testid="clock-in-selfie-button"
          className="w-full rounded-2xl bg-slate-900 text-white p-6 flex items-center gap-5 hover:bg-slate-800 active:scale-[0.99] transition-[background-color,transform]">
          <div className="h-16 w-16 rounded-full bg-emerald-500 grid place-items-center shadow-lg shadow-emerald-500/30"><Fingerprint className="h-8 w-8" /></div>
          <div className="text-left">
            <div className="font-heading text-2xl font-extrabold">Clock In</div>
            <div className="text-sm text-slate-400">GPS lock + selfie verification</div>
          </div>
        </button>
      )}
      {inNow && (
        <Button variant="outline" onClick={() => setMode("out")} className="w-full h-12 border-rose-300 text-rose-600 hover:bg-rose-50" data-testid="clock-out-button">
          <LogOut className="h-4 w-4 mr-2" />Clock Out (selfie + GPS)
        </Button>
      )}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2"><Timer className="h-4 w-4 text-sky-600" />Active Tasks</h2>
          <Link to="/app/tasks" className="text-sm font-semibold text-sky-700" data-testid="view-all-tasks-link">View all</Link>
        </div>
        <div className="space-y-3">
          {tasks.length === 0 && <div className="text-sm text-slate-500 bg-white border border-dashed rounded-xl p-6 text-center" data-testid="no-active-tasks">No active tasks assigned to you.</div>}
          {tasks.map((t, i) => <TaskCard key={t.id} t={t} i={i} />)}
        </div>
      </section>
      <ClockInDialog open={!!mode} mode={mode || lastMode} onOpenChange={(o) => !o && setMode(null)} onDone={setRec} />
    </div>
  );
}
